import {
    InfiniteData,
    keepPreviousData,
    QueryClient,
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import {
    archiveCandidate,
    bulkStoreCandidate,
    fetchCandidate,
    fetchCandidates,
    fetchMissedCandidates,
    rejectCandidate,
    resolveMissedCandidate,
    retryMissedCandidate,
    storeCandidate,
    unarchiveCandidate,
    updateCandidateColumn,
    updateCandidateCompanyColumn,
} from './api'
import { onErrorNotifyAboutValidationError } from '../util'
import { useAuth } from '../auth'
import { Candidate, CandidateQuery } from './candidate'
import { PaginationResponse } from '../zod/pagination'
import { useEffect } from 'react'
import { echo } from '../echo'

export function useCandidates({ query, enabled }: { query?: CandidateQuery; enabled?: boolean } = {}) {
    const { hasSubscription } = useAuth()

    return useQuery({
        queryFn: () => fetchCandidates({ query }),
        queryKey: ['candidates', query],
        placeholderData: keepPreviousData,
        enabled: hasSubscription && enabled,
    })
}

export function useCandidatesForColumn(campaignId: string, columnId: string, pageSize = 15) {
    const { hasSubscription } = useAuth()

    const query: CandidateQuery = {
        filters: {
            campaignId: [campaignId],
            columnId: [columnId],
        },
        page: 1,
        size: pageSize,
        sort: 'column_order',
    }

    return useQuery({
        queryFn: () => fetchCandidates({ query }),
        queryKey: ['candidates', 'column', campaignId, columnId, pageSize],
        placeholderData: keepPreviousData,
        enabled: hasSubscription && !!campaignId && !!columnId,
    })
}

export function useCandidatesForColumnInfinite({
    campaignId,
    columnId,
    pageSize = 20,
}: {
    campaignId?: string
    columnId: string
    pageSize?: number
}) {
    const { hasSubscription } = useAuth()

    return useInfiniteQuery({
        queryKey: ['candidates', 'column', campaignId, columnId, 'infinite', pageSize],
        queryFn: ({ pageParam = 1 }) => {
            const query: CandidateQuery = {
                filters: {
                    campaignId: campaignId ? [campaignId] : undefined,
                    [!campaignId ? 'companyColumnId' : 'columnId']: [columnId],
                },
                include: !campaignId ? ['campaign'] : [],
                page: pageParam,
                size: pageSize,
                sort: !campaignId ? 'company_column_order' : 'column_order',
            }
            return fetchCandidates({ query })
        },
        getNextPageParam: (lastPage) => {
            if (lastPage.meta.currentPage < lastPage.meta.lastPage) {
                return lastPage.meta.currentPage + 1
            }
            return undefined
        },
        initialPageParam: 1,
        enabled: hasSubscription && !!columnId,
    })
}

export function useCandidate(candidateId?: string, options?: { refetchInterval?: number }) {
    const { hasSubscription } = useAuth()

    return useQuery({
        queryFn: () => fetchCandidate(candidateId!),
        queryKey: ['candidates', candidateId],
        refetchInterval: options?.refetchInterval,
        enabled: hasSubscription && !!candidateId,
    })
}

/**
 * Loads the candidate again whenever its conversation gets a new message, for as long as it is open.
 */
export function useLiveConversation(candidateId?: string) {
    const queryClient = useQueryClient()

    useEffect(() => {
        if (!candidateId) {
            return
        }

        const channel = `candidate.${candidateId}`
        let left = false

        echo().then((connection) => {
            if (left) {
                return
            }

            connection.private(channel).listen('.conversation.updated', () => {
                queryClient.invalidateQueries({ queryKey: ['candidates', candidateId] })
            })
        })

        return () => {
            left = true
            echo().then((connection) => connection.leave(channel))
        }
    }, [candidateId, queryClient])
}

export function useStoreCandidate(campaignId: string, missedCandidateId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: missedCandidateId
            ? retryMissedCandidate(campaignId, missedCandidateId)
            : storeCandidate(campaignId),
        onSuccess: () => {
            if (missedCandidateId) {
                queryClient.invalidateQueries({ queryKey: ['missed-candidates', campaignId] })
            }

            queryClient.invalidateQueries({
                queryKey: ['dashboard'],
                exact: false,
            })

            queryClient.invalidateQueries({
                queryKey: ['candidates'],
                exact: false,
            })
        },
    })
}

export function useMissedCandidates(campaignId: string, query?: { page?: number; size?: number }) {
    return useQuery({
        queryFn: () => fetchMissedCandidates(campaignId, query),
        queryKey: ['missed-candidates', campaignId, query],
        placeholderData: keepPreviousData,
        enabled: !!campaignId,
    })
}

export function useResolveMissedCandidate(campaignId: string, missedCandidateId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: { candidateId: string; missedCandidateId?: string }) =>
            resolveMissedCandidate(campaignId, missedCandidateId || input.missedCandidateId!)(input),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['missed-candidates', campaignId],
                exact: false,
            })
        },
    })
}

export function useRejectCandidate(candidateId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: { candidateId?: string; messageTemplateId: string }) =>
            rejectCandidate(candidateId || input.candidateId!, {
                messageTemplateId: input.messageTemplateId,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['candidates', candidateId],
                exact: true,
            })
        },
    })
}

export function useBulkStoreCandidate(campaignId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: bulkStoreCandidate(campaignId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns', campaignId],
                exact: true,
            })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}

export function useArchiveCandidate() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: archiveCandidate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns'],
                exact: false,
            })
            queryClient.invalidateQueries({
                queryKey: ['candidates'],
                exact: false,
            })
        },
    })
}

export function useUnarchiveCandidate() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: unarchiveCandidate,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['campaigns'],
                exact: false,
            })
            queryClient.invalidateQueries({
                queryKey: ['candidates'],
                exact: false,
            })
        },
    })
}

export function useUpdateCandidateColumn(candidateId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: updateCandidateColumn(candidateId),
        onSuccess: (candidate, variables) => {
            invalidateBoardCandidates({
                campaignId: candidate.campaignId,
                orderColumn: 'columnOrder',
                columnIdColumn: 'columnId',
                queryClient,
                fromColumnId: variables.fromColumnId,
                candidate,
            })
        },
    })
}

export function useCandidatesForCompanyColumn(companyId: string, columnId: string, pageSize = 15) {
    const { hasSubscription } = useAuth()

    const query: CandidateQuery = {
        filters: {
            columnId: [columnId],
        },
        include: ['campaign'],
        page: 1,
        size: pageSize,
        sort: 'column_order',
    }

    return useQuery({
        queryFn: () => fetchCandidates({ query }),
        queryKey: ['candidates', 'company-column', companyId, columnId, pageSize],
        placeholderData: keepPreviousData,
        enabled: hasSubscription && !!companyId && !!columnId,
    })
}

export function useUpdateCandidateCompanyColumn(candidateId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: updateCandidateCompanyColumn(candidateId),
        onSuccess: (candidate, variables) => {
            invalidateBoardCandidates({
                campaignId: undefined,
                orderColumn: 'companyColumnOrder',
                columnIdColumn: 'companyColumnId',
                queryClient,
                fromColumnId: variables.fromColumnId,
                candidate,
            })
        },
    })
}

function invalidateBoardCandidates({
    campaignId,
    orderColumn,
    columnIdColumn,
    queryClient,
    fromColumnId,
    candidate,
}: {
    campaignId?: string
    orderColumn: keyof Candidate
    columnIdColumn: keyof Candidate
    queryClient: QueryClient
    fromColumnId: string
    candidate: Candidate
}) {
    // Remove candidate from old column cache (if column changed)
    if (fromColumnId !== candidate[columnIdColumn]) {
        queryClient.setQueriesData<InfiniteData<PaginationResponse<Candidate>>>(
            {
                queryKey: ['candidates', 'column', campaignId, fromColumnId, 'infinite'],
            },
            (oldData) => {
                if (!oldData?.pages) return oldData
                return {
                    ...oldData,
                    pages: oldData.pages.map((page) => ({
                        ...page,
                        data: page.data.filter((c) => c.id !== candidate.id),
                    })),
                }
            }
        )

        queryClient.setQueriesData<InfiniteData<PaginationResponse<Candidate>>>(
            {
                queryKey: ['candidates', 'column', campaignId, fromColumnId, 'infinite'],
            },
            (oldData) => {
                if (!oldData?.pages) return oldData
                return {
                    ...oldData,
                    pages: oldData.pages.map((page) => ({
                        ...page,
                        data: page.data.filter((c) => c.id !== candidate.id),
                    })),
                }
            }
        )
    }

    // Update infinite queries
    queryClient.setQueriesData<InfiniteData<PaginationResponse<Candidate>>>(
        { queryKey: ['candidates', 'column', campaignId, candidate[columnIdColumn], 'infinite'] },
        (oldData) => {
            if (!oldData?.pages) return oldData

            // Remove from all pages if exists
            let candidateFound = false
            const pagesWithoutCandidate = oldData.pages.map((page) => {
                const filtered = page.data.filter((c) => {
                    if (c.id === candidate.id) {
                        candidateFound = true
                        return false
                    }
                    return true
                })
                return { ...page, data: filtered }
            })

            // If moving to this column (not just reordering), add to first page
            if (fromColumnId !== candidate[columnIdColumn] && pagesWithoutCandidate.length > 0) {
                const firstPage = pagesWithoutCandidate[0]
                pagesWithoutCandidate[0] = {
                    ...firstPage,
                    data: [candidate, ...firstPage.data].sort((a, b) => {
                        const aOrder = a[orderColumn] || ''
                        const bOrder = b[orderColumn] || ''
                        if (aOrder < bOrder) return -1
                        if (aOrder > bOrder) return 1
                        return 0
                    }),
                }
            } else if (candidateFound) {
                // Just reordering within same column - add back and resort
                const candidateOrder = candidate[orderColumn] || ''
                const targetPageIndex = pagesWithoutCandidate.findIndex((page) =>
                    page.data.some((c) => (c[orderColumn] || '') > candidateOrder)
                )

                if (targetPageIndex >= 0) {
                    const targetPage = pagesWithoutCandidate[targetPageIndex]
                    pagesWithoutCandidate[targetPageIndex] = {
                        ...targetPage,
                        data: [...targetPage.data, candidate].sort((a, b) => {
                            const aOrder = a[orderColumn] || ''
                            const bOrder = b[orderColumn] || ''
                            if (aOrder < bOrder) return -1
                            if (aOrder > bOrder) return 1
                            return 0
                        }),
                    }
                } else {
                    // Add to last page if no better place found
                    const lastPageIndex = pagesWithoutCandidate.length - 1
                    const lastPage = pagesWithoutCandidate[lastPageIndex]
                    pagesWithoutCandidate[lastPageIndex] = {
                        ...lastPage,
                        data: [...lastPage.data, candidate].sort((a, b) => {
                            const aOrder = a[orderColumn] || ''
                            const bOrder = b[orderColumn] || ''
                            if (aOrder < bOrder) return -1
                            if (aOrder > bOrder) return 1
                            return 0
                        }),
                    }
                }
            }

            return {
                ...oldData,
                pages: pagesWithoutCandidate,
            }
        }
    )
}
